import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Textarea
} from '@chakra-ui/react'
import moment from 'moment-timezone'

import axios from 'lib/axios'
import { isAllowedLiabilityType } from 'lib/liability-types'
import { RentalDetailsContext } from './RentalDetailsContext'

export default function ScheduleChargeModal({
  finalLiabilityDate,
  isOpen,
  onClose,
  onSuccess,
  occupancyDate,
  rentalId
}: {
  finalLiabilityDate?: string | null
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  occupancyDate: string
  rentalId: string
}) {
  const { liabilityTypes } = useContext(RentalDetailsContext)
  const [liabilityTypeId, setLiabilityTypeId] = useState('')
  const [amount, setAmount] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const tomorrow = moment().tz('America/New_York').add(1, 'day').format('YYYY-MM-DD')
  const selectableTypes = useMemo(
    () =>
      liabilityTypes
        .filter(isAllowedLiabilityType)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [liabilityTypes]
  )
  const selectedLiabilityType = selectableTypes.find((type) => type.id === liabilityTypeId)

  useEffect(() => {
    if (!isOpen) return
    setLiabilityTypeId('')
    setAmount('')
    setDueDate(tomorrow)
    setNote('')
    setError('')
  }, [isOpen, tomorrow])

  const validAmount = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0
  const canSubmit =
    Boolean(liabilityTypeId) &&
    validAmount &&
    dueDate >= tomorrow &&
    dueDate >= occupancyDate &&
    (!finalLiabilityDate || dueDate <= finalLiabilityDate) &&
    Boolean(note.trim()) &&
    !saving

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    setError('')
    try {
      await axios.post('/admin/scheduled_rental_liabilities', {
        rental_id: rentalId,
        liability_type_id: liabilityTypeId,
        amount: Number(amount),
        due_date: dueDate,
        note: note.trim()
      })
      onClose()
      onSuccess()
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message ?? requestError.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Schedule charge</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {error && (
              <Alert status='error'>
                <AlertIcon />
                {error}
              </Alert>
            )}
            <FormControl isRequired>
              <FormLabel>Liability type</FormLabel>
              <Select
                placeholder='Select a liability type'
                value={liabilityTypeId}
                onChange={(event) => {
                  const id = event.target.value
                  setLiabilityTypeId(id)
                  const type = selectableTypes.find((candidate) => candidate.id === id)
                  setAmount(type?.price == null ? '' : Number(type.price).toFixed(2))
                }}
              >
                {selectableTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Amount</FormLabel>
              <Input
                isDisabled={selectedLiabilityType?.is_variable === false}
                min='0.01'
                step='0.01'
                type='number'
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Due date</FormLabel>
              <Input
                min={tomorrow > occupancyDate ? tomorrow : occupancyDate}
                max={finalLiabilityDate ?? undefined}
                type='date'
                value={dueDate}
                onInput={(event) => setDueDate(event.currentTarget.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Explanation</FormLabel>
              <Textarea value={note} onChange={(event) => setNote(event.target.value)} />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button colorScheme='teal' isDisabled={!canSubmit} isLoading={saving} onClick={submit}>
            Schedule charge
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
